import Vue from "vue";
import router from "@/router";
import { authHTTP } from "@/services/axios.service";
import { VueOfflineStorage } from "vue-offline";
import { HandleFilters, MutateFilter } from "@/services/filters.service";
import { handleError } from "@/functions/handleError.function";

// initial state
const state = {
  exams: {},
  items: {},
  kdrs: {},
  examList: [],
  openExams: [],
  examTemplates: [],
  loading: {
    examList: false,
    openExams: false,
    exam: false,
    kdr: false,
    items: false
  },
  filters: {},
  pages: {}
};
// getters
const getters = {
  loading: state => {
    return state.loading;
  },
  filters: state => {
    return state.filters;
  },
  search: state => {
    return state.filters.search;
  },
  examList: state => {
    return state.examList;
  },
  pages: state => {
    return state.pages;
  },
  exams: state => {
    return state.exams;
  },
  items: state => id => {
    return state.items[id];
  },
  kdr: state => id => {
    return state.kdrs[id];
  },
  examTemplates: state => {
    return state.examTemplates;
  }
};
// actions (async)
const actions = {
  getExam(context, payload) {
    authHTTP
      .get("exams/" + payload.id + "/")
      .then(function(response) {
        // Vue.set(object, propertyName, value)
        Vue.set(state.exams, payload.id, response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getExamKDR(context, payload) {
    authHTTP
      .get("/exams/" + payload.id + "/kdr/")
      .then(function(response) {
        Vue.set(state.kdrs, payload.id, response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getItems(context, payload) {
    authHTTP
      .get("exams/" + payload.id + "/items/")
      .then(function(response) {
        Vue.set(state.items, payload.id, response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getExamList(context, payload) {
    const pageSize = context.getters["core/pageSize"];
    var examFilter = undefined;
    if (payload) {
      examFilter = HandleFilters(payload);
    } else {
      examFilter = HandleFilters(state.filters, pageSize);
    }
    authHTTP
      .get("exams/" + examFilter)
      .then(function(response) {
        state.examList = response.data.results;
        var pages = Object.assign({}, response.data);
        delete pages.results;
        Vue.set(state.pages, "examList", pages);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getExamDashboard(context) {
    authHTTP
      .get("exams/dashboard/")
      .then(function(response) {
        state.openExams = response.data;
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  createExam(context, payload) {
    const redirect = payload.redirect;
    delete payload.redirect;
    return authHTTP
      .post("exams/", payload)
      .then(function(response) {
        if (redirect == "exam-detail") {
          router.push({ name: "exam-detail", params: { id: response.data.id } });
        } else {
          router.push({ name: "exam-index" });
        }
        return;
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
        return;
      });
  },
  updateItem(context, payload) {
    const items = state.items[payload.examId];
    var item = items.find(i => i.id == payload.itemId);
    var data = { ...payload };
    delete data.itemId;
    delete data.examId;
    for (let key in data) {
      Vue.set(item, key, data[key]);
    }
    // **IMPORTANT**
    //
    // For now we just ASSUME that this works. It needs an offline capability
    // built, but that will take a few days more thought.
    const path = "exams/" + payload.examId + "/items/" + payload.itemId + "/";
    authHTTP
      .patch(path, data)
      .then(function() {
        // TODO: Handle status other than 200
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  closeExam(context, payload) {
    /* Close the exam when it is submitted.

    The logic of this function is that:

    - The answer for each item is saved *again* on the server.

      This is done in case the automated save failed so that the correct answer
      is recorded for when the exam is marked

    - The exam is then updated with is_closed = true to close the exam.

      This triggers the API to automatically mark the exam and it then returns
      the updated exam after is has been marked and, if appropriate, the result
      assigned.
    */
    context.commit("core/clearErrors", {}, { root: true });
    // Create a stack of Promises to update all of the items
    let stack = [];
    for (let item of payload.items) {
      let path = "/exams/" + payload.id + "/items/" + item.id + "/";
      let data = {
        examId: payload.id,
        itemId: item.id
      };
      // Different payloads are sent when the exam is submitted depending on
      // whether it's the candidate or supervisor submitting the exam
      if (payload.isSupervisor) {
        data["assessment"] = item.assessment;
      } else {
        data["answer"] = item.answer;
      }
      stack.push(authHTTP.patch(path, data));
    }
    // Update *ALL* items, *then* close the exam
    Promise.all(stack)
      .then(function() {
        return authHTTP
          .patch("/exams/" + payload.id + "/", { is_closed: true })
          .then(function(response) {
            // Update the model of the exam as the server may mark and/or
            // otherwise change the exam before it returns it to us
            Vue.set(state.exams, payload.id, response.data);
            // Also update the items as their assessments have changed
            //store.dispatch("exams/getItems", { id: payload.id });
            return true;
          })
          .catch(function(error) {
            let data = handleError(error);
            context.commit("core/setError", data, { root: true });
            return false;
          });
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
        return false;
      });
  },
  signExam(context, payload) {
    authHTTP
      .patch("/exams/" + payload.id + "/sign/", {
        username: payload.username,
        signature: payload.signature
      })
      .then(function(response) {
        const sigField = payload.role;
        const dtField = payload.role + "_dt";
        Vue.set(state.exams[payload.id], sigField, response.data["digest"]);
        Vue.set(state.exams[payload.id], dtField, response.data["timestamp"]);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  },
  getExamTemplates(context) {
    authHTTP
      .get("/exams/templates/")
      .then(function(response) {
        context.commit("setExamTemplates", response.data);
        VueOfflineStorage.set("examTemplates", response.data);
      })
      .catch(function(error) {
        let data = handleError(error);
        context.commit("core/setError", data, { root: true });
      });
  }
};

// Mutations (sync)
const mutations = {
  clearState() {
    state.examList = [];
    state.openExams = [];
    state.exams = {};
    state.items = {};
  },
  setLoadingFlag() {
    state.loading.examList = true;
    state.loading.openExams = true;
  },
  filters(state, payload) {
    MutateFilter(state, payload);
  },
  removeItems(state, payload) {
    // payload: exam.id
    Vue.delete(state.items, payload);
  },
  setExamTemplates(state, payload) {
    state.examTemplates = payload;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
